import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LogIn, Loader2, AlertCircle } from 'lucide-react';
import { validateAccessCode } from '../lib/airtable';
interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}
export function LoginModal({
  isOpen,
  onClose,
  onSuccess
}: LoginModalProps) {
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await validateAccessCode(accessCode.trim());
      if (result.valid) {
        // Store access info in localStorage
        localStorage.setItem('sponsrai_access', accessCode.trim());
        if (result.user) {
          localStorage.setItem('sponsrai_user', JSON.stringify(result.user));
        }
        onSuccess();
      } else {
        setError(result.error || 'Invalid access code. Please check your email for the correct code.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setIsLoading(false);
  };
  return <AnimatePresence data-id="element-785">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-786">
            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} onClick={e => e.stopPropagation()} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden" data-id="element-787">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-800" data-id="element-788">
                <div data-id="element-789">
                  <h2 className="text-xl font-bold text-white" data-id="element-790">Welcome Back</h2>
                  <p className="text-sm text-slate-400 mt-1" data-id="element-791">
                    Enter your access code to continue
                  </p>
                </div>
                <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors" data-id="element-792">
                  <X size={20} data-id="element-793" />
                </button>
              </div>

              {/* Body */}
              <form onSubmit={handleSubmit} className="p-6" data-id="element-794">
                <div className="mb-6" data-id="element-795">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-796">
                    Access Code
                  </label>
                  <input type="text" value={accessCode} onChange={e => {
                setAccessCode(e.target.value);
                setError('');
              }} placeholder="e.g. BR-ACME-X7K2" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all font-mono" autoFocus data-id="element-797" />
                  {error && <div className="mt-3 flex items-start gap-2 text-rose-400 text-sm" data-id="element-798">
                      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" data-id="element-799" />
                      <span data-id="element-800">{error}</span>
                    </div>}
                </div>

                <button type="submit" disabled={!accessCode.trim() || isLoading} className="w-full py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 rounded-full font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-801">
                  {isLoading ? <>
                      <Loader2 size={18} className="animate-spin" data-id="element-802" />{' '}
                      Verifying...
                    </> : <>
                      <LogIn size={18} data-id="element-803" /> Access Dashboard
                    </>}
                </button>

                <p className="mt-6 text-center text-sm text-slate-500" data-id="element-804">
                  Don't have an access code?{' '}
                  <button type="button" onClick={onClose} className="text-cyan-400 hover:text-cyan-300 transition-colors" data-id="element-805">
                    Join the waitlist
                  </button>
                </p>
              </form>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}